import React, {useState} from 'react';

// global components
import {SearchBar} from "../../../../designSystem/components/SearchBar";
import {LoadingView} from "../../../../designSystem/components/LoadingView";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {Dialog} from "../../../../designSystem/components/Dialog";
import {TotalDisplay} from "../../../../components/PaginationBar";
import {StandardLayout} from "../../../../components/StandardLayout";

// local components
import {UserModalContent, USER_MODAL_CONTENT} from "../UserModalContent";
import {UserTable} from '../UserTable';

// types
import {UserOrganization} from "../../model/types";
import {PERMISSIONS} from "../../../../utils/constants";

// gooks
import {useExportUserManagement} from "../../hooks/useExportUserManagement";
import {useUserOrganizations} from "../../hooks/useUserOrganizations";
import {useUserMutations} from "../../hooks/useUserMutations";
import {useUserFilters} from "../../hooks/useUserFilters";
import {useUserListView} from "../../hooks/useUserListView";

const DEFAULT_PERMISSIONS = PERMISSIONS.reduce((acc, curr) => {
  acc[curr.name] = curr.default;
  return acc;
}, {});

const ALL_UNCHECKED_PERMISSIONS = PERMISSIONS.reduce((acc, curr) => {
  acc[curr.name] = false;
  return acc;
}, {});

const DEFAULT_ORGANIZATION = { id: '', name: '' };
const DEFAULT_USER = {name: '', email: '', organization:DEFAULT_ORGANIZATION, permissions: DEFAULT_PERMISSIONS}

const applyUserPermissions = (userPermissions, allPermissions) => {
  Object.keys(userPermissions).forEach(key => {
    if (userPermissions[key] === true && key in allPermissions) {
      allPermissions[key] = true;
    }
  });
  return allPermissions;
}

export const UserManagementFeature = ({userRole, userToken}) => {
  const {
    users,
    organizations,
    isLoading,
    fetchUsers,
  } = useUserOrganizations(userToken);
  const [editingUser, setEditingUser] = useState<UserOrganization | null>(null);
  const [editedName, setEditedName] = useState<string>('');
  const [editedRole, setEditedRole] = useState<string>('');
  const [editedPermissions, setEditedPermissions] = useState({});
  const [newUser, setNewUser] = useState(DEFAULT_USER);
  const [editedOrganizations, setEditedOrganizations] = useState(DEFAULT_ORGANIZATION);
  const [dialogContent, setDialogContent] = useState<USER_MODAL_CONTENT | ''>('');
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState('');

  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'fail'>('success');
  const {
    searchTerm,
    setSearchTerm,
    sortColumn,
    sortOrder,
    handleSort,
  } = useUserFilters();

  const {
    handleAddUser,
    handleUpdateUser,
    handleDeleteUser,
    handleResetPassword,
  } = useUserMutations({
    userToken,
    newUser,
    selectedRole,
    editingUser,
    editedName,
    editedRole,
    editedPermissions,
    editedOrganizations,
    fetchUsers,
    setDialogContent,
    setMessage,
    setMessageType,
    setEditingUser,
  });

  // trigger when the dialog backdrop is clicked
  const onDialogBackdropClick = () => {
    if (
      dialogContent === USER_MODAL_CONTENT.ADD ||
      dialogContent === USER_MODAL_CONTENT.EDIT ||
      dialogContent === USER_MODAL_CONTENT.CONFIRM_DELETE
    ) {
      setDialogContent('')
    }
  }

  // trigger when the create organization button is clicked
  const onClickCreateUser = () => {
    setDialogContent(USER_MODAL_CONTENT.ADD)
  }

  // trigger when the edit user button is clicked
  const onClickEditUser = (user: UserOrganization) => {
    setEditingUser(user);
    setEditedName(user.name);
    setEditedRole(user.role);
    setEditedPermissions(user.permissions ? applyUserPermissions(user.permissions, ALL_UNCHECKED_PERMISSIONS) : {});
    setEditedOrganizations(organizations.find(org => user.organization === org.name) || DEFAULT_ORGANIZATION);
    setDialogContent(USER_MODAL_CONTENT.EDIT);
  }

  // trigger when the delete user button is clicked
  const onClickDeleteUser = (user: UserOrganization) => {
    setDeletingUserId(user.id);
    setDialogContent(USER_MODAL_CONTENT.CONFIRM_DELETE);
  }

  const handleChangeNewUser = (obj) => setNewUser({...newUser, ...obj})
  const handleChangeRole = (value: string) => setSelectedRole(value)
  const handleChangeEditedName = (value: string) => setEditedName(value)
  const handleChangeEditedRole = (value: string) => setEditedRole(value)
  const handleChangeEditedPermissions = (value) => setEditedPermissions(value)
  const handleChangeEditedOrganizations = (value) => setEditedOrganizations(value)
  const handleCloseDialog = () => setDialogContent('');

  // trigger when the ok button in the message dialog is clicked
  const handleClickOkButton = async () => {
    setDialogContent('')
  }

  const {filteredUsers, sortedUsers} = useUserListView({
    users,
    searchTerm,
    sortColumn,
    sortOrder,
  });

  const { exportData } = useExportUserManagement({ data: filteredUsers })

  // Rendering
  if (isLoading)
    return <LoadingView/>

  return (
    <React.Fragment>
      <StandardLayout
        headerTitle="帳號管理"
        headerActionLabel="新增帳號"
        headerActionOnClick={onClickCreateUser}
        body={
          <div className="w-full m-auto flex flex-col gap-4">
            <SearchBar
              placeholder="搜尋業務組別名稱/姓名/Email"
              value={searchTerm}
              onChangeValue={(value: string) => setSearchTerm(value)}
            />

            <div className="flex flex-row justify-between items-center">
              <TotalDisplay numberOfData={filteredUsers?.length}/>
              <ActionButton
                label="匯出資料"
                onClick={exportData}
                iconName="fa-file-export"
                variant="outline"
              />
            </div>

            {/* the user table */}
            {filteredUsers?.length > 0
              ? <UserTable
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                users={sortedUsers}
                handleSort={handleSort}
                handleEdit={onClickEditUser}
                handleDelete={onClickDeleteUser}
                handleResetPassword={handleResetPassword}
              />
              : null
            }
          </div>
        }
      />
      <Dialog
        isOpen={dialogContent !== ''}
        onBackdropClick={onDialogBackdropClick}
      >
        {UserModalContent({
          message,
          messageType,
          newUser,
          editedName,
          editedOrganizations,
          editedRole,
          editedPermissions,
          organizations,
          selectedRole,
          userRole,
          deletingUserId,
          handleChangeNewUser,
          handleChangeRole,
          handleChangeEditedName,
          handleChangeEditedOrganizations,
          handleChangeEditedRole,
          handleChangeEditedPermissions,
          handleUpdateUser,
          handleAddUser,
          handleDeleteUser,
          handleCloseDialog,
          handleClickOkButton
        })[dialogContent] || <></>}
      </Dialog>
    </React.Fragment>
  )
};
